import { useMutation } from "@apollo/client";
import { DELETE_EDITION, UPDATE_EDITION } from "./graphql";
import { PropsWithChildren } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button, Container, TextField } from "@mui/material";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  FestivalEdition,
} from "../../../__generated__/graphql";
import { PageContainer } from "../../../components/PageContainer";
import { ButtonContainer } from "../../../components/ButtonContainer";
import { confirmAlert } from "react-confirm-alert";
import { useNavigate } from "react-router-dom";
import { Info } from "../../../components/Info";
import dayjs from "dayjs";
import { ZonedDateTimePicker } from "../../../components/ZonedDateTimePicker";
import { TimeZonePicker } from "../../../components/TimezonePicker";
import { GET_EDITIONS } from "../../editions/graphql";

const schema = yup
  .object({
    name: yup.string().required(),
    year: yup
      .string()
      .required("Year is required")
      .matches(/^\d{4}$/, "Enter a 4 digit year"),
    availabilityDate: yup.string().required(),
    timezone: yup.string().required(),
  })
  .required();

const EditionForm = ({ edition }: { edition: Partial<FestivalEdition> }) => {
  const navigate = useNavigate();
  const [updateEdition] = useMutation(UPDATE_EDITION, {
    onCompleted: () => {
      toast("edition updated");
    },
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: edition.name!,
      year: `${edition.year ?? ""}`,
      availabilityDate: edition.availabilityDate ?? new Date().toISOString(),
      timezone: edition.timezone!,
    },
  });
  
  const [deleteEdition] = useMutation(DELETE_EDITION, {
    onCompleted: () => {
      toast("editon deleted");
      navigate("/editions", { replace: true });
    },
    refetchQueries: [GET_EDITIONS]
  });
  
  const onSubmit = (val: any) => {
    const { name, year, availabilityDate, timezone } = val;
    updateEdition({
      variables: {
        id: edition.id!,
        name,
        year,
        availabilityDate: availabilityDate,
        timezone,
      },
    });
  };
  const handleDelete = () => {
    confirmAlert({
      title: "Delete this edition?",
      message: "Are you sure? this can't be undone",
      buttons: [
        {
          label: "Yes",
          onClick: () =>
            deleteEdition({ variables: { editionId: edition.id! } }),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  return (
    <PageContainer title="Edit edition details">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container style={{ flexDirection: "column", display: "flex" }}>
          <TextField
            id="outlined-basic"
            label="Name"
            variant="outlined"
            defaultValue={edition?.name}
            {...register("name")}
          />
          <p>{errors.name?.message ?? ""}</p>
          <TextField
            id="outlined-basic"
            label="Year"
            variant="outlined"
            defaultValue={edition?.year}
            type="number"
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 1900 }}
            placeholder="2026"
            {...register("year")}
          />
          <p>{errors.year?.message ?? ""}</p>
          <Controller
            control={control}
            name="availabilityDate"
            render={({ field: { onChange } }) => (
              <ZonedDateTimePicker
                format="YYYY/MM/DD HH:mm"
                ampm={false}
                label="Set times visible on"
                // @ts-ignore
                defaultValue={dayjs(edition?.availabilityDate)}
                // @ts-ignore
                onChange={onChange} // send value to hook form
              >
                <FaqLabel style={{ marginTop: 4 }}>
                  The date in which band times become visible in the app.
                </FaqLabel>
              </ZonedDateTimePicker>
            )}
          />

          <p>{errors.availabilityDate?.message}</p>

          <Controller
            {...register(`timezone`)}
            name={`timezone`}
            control={control}
            render={({ field: { onChange, value } }) => (
              // @ts-ignore
              <TimeZonePicker onChange={onChange} value={value} />
            )}
          />
          <FaqLabel>The timezone of the festival</FaqLabel>
          <p>{errors.timezone?.message}</p>
          <ButtonContainer>
            <Button type="submit" variant="contained">
              Save
            </Button>
            <Button
              variant="contained"
              onClick={handleDelete}
              style={{ backgroundColor: "red" }}
            >
              Delete
            </Button>
          </ButtonContainer>
        </Container>
      </form>
      <Info />
    </PageContainer>
  );
};

export default EditionForm;

export const FaqLabel: React.FC<
  PropsWithChildren<{ style?: React.CSSProperties }>
> = ({ children, style }) => (
  <label
    style={{
      marginLeft: 8,
      marginTop: -10,
      border: "1px solid black",
      backgroundColor: "rgba(	173,216,230, 0.5)",
      padding: 8,
      borderRadius: 8,
      width: "fit-content",
      ...style,
    }}
  >
    {children}
  </label>
);
